/* ============================================================================
   zh-theme-toggle.js
   ZeroHedge Store — theme toggle module
   ============================================================================

   TPS COVER SHEET
   file:        assets/zh-theme-toggle.js
   md5:         5f2dde21189c84bd33013dfc04f0c8eb
   size:        7508 bytes
   session:     UX1
   phase:       Deliverable 2 of 3 — theme toggle (module of 3 files)
   date:        2026-05-20
   routes_to:   ACS9 (ratification) → Dan (deploy to sandbox)
   supersedes:  — (net-new file)
   locks:       Theme-toggle behavior contract — click handling,
                persistence, aria-label sync, system-pref fallback,
                global ZHTheme API
   base:        new file, no base

   ----------------------------------------------------------------------------
   Authority:
     UX1-IMPLEMENTATION-HANDOFF.md §0 (locked light/dark theme toggle)
     ACS9-to-UX1-Mandate-Expansion-v2.md §3 Phase 1 Deliverable 2
     ACS9 Deliverable 2 greenlight (this turn) — three-component build

   ----------------------------------------------------------------------------
   How this fits with the head script:

     A blocking <script> in theme.liquid <head> runs synchronously BEFORE
     any stylesheet load. It reads localStorage → prefers-color-scheme →
     'dark' (brand default) and sets the [data-theme] attribute on
     <html> so the token vars in zh-design-tokens.css resolve to the
     correct mode on first paint (no flash of wrong theme).

     THIS module loads via <script defer> after the body parses. Its job
     is ongoing behaviour: user clicks, persistence on change, label
     updates, and following the system when no explicit user choice has
     been made.

   ----------------------------------------------------------------------------
   Markup contract — any element with the data-zh-theme-toggle attribute
   becomes a toggle. Multiple toggles (header + footer + mobile drawer)
   are supported; all stay in sync.

     <button type="button" data-zh-theme-toggle aria-label="Toggle theme">
       <svg class="zh-theme-toggle__icon zh-theme-toggle__icon--sun">...</svg>
       <svg class="zh-theme-toggle__icon zh-theme-toggle__icon--moon">...</svg>
     </button>

   Icon visibility is CSS-driven (see header.liquid {% style %} block).
   This module does not manipulate icon visibility — that's the CSS's
   job, gated on [data-theme] on <html>.

   ----------------------------------------------------------------------------
   Public API exposed on window.ZHTheme:

     ZHTheme.get()         → 'dark' | 'light'    current theme
     ZHTheme.set(theme)    → void                 set explicitly + persist
     ZHTheme.toggle()      → void                 flip + persist

   Custom event dispatched on <html> when the theme changes:

     document.documentElement.addEventListener('zh:theme-change', function (e) {
       console.log('theme is now', e.detail.theme);
     });

   ============================================================================ */

(function () {
  'use strict';

  // ---- Constants ----------------------------------------------------------
  var STORAGE_KEY  = 'zh_theme';
  var ATTR         = 'data-theme';
  var EVENT        = 'zh:theme-change';
  var DARK         = 'dark';
  var LIGHT        = 'light';
  var ARIA_TO_LIGHT = 'Switch to light theme';
  var ARIA_TO_DARK  = 'Switch to dark theme';
  var TOGGLE_SELECTOR = '[data-zh-theme-toggle]';

  // ---- Storage layer ------------------------------------------------------
  // localStorage can throw (private mode, quota, blocked). All access
  // is wrapped — failures degrade to in-session-only theme changes.

  function readStored() {
    try {
      var v = localStorage.getItem(STORAGE_KEY);
      return (v === DARK || v === LIGHT) ? v : null;
    } catch (e) {
      return null;
    }
  }

  function writeStored(theme) {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {
      /* private mode / quota / blocked — no-op; theme still applied for
         this session via the data-theme attribute */
    }
  }

  // ---- Theme state --------------------------------------------------------

  function currentTheme() {
    return document.documentElement.getAttribute(ATTR) === LIGHT ? LIGHT : DARK;
  }

  function setTheme(theme, persist) {
    var html = document.documentElement;
    html.setAttribute(ATTR, theme);
    if (persist) {
      writeStored(theme);
    }
    syncToggles(theme);
    try {
      html.dispatchEvent(new CustomEvent(EVENT, { detail: { theme: theme } }));
    } catch (e) {
      /* CustomEvent unsupported in very old browsers; event dispatch is
         informational and safe to skip. */
    }
  }

  // ---- Toggle UI sync -----------------------------------------------------
  // Updates aria-label on every toggle button to reflect the action the
  // click will take, not the current state. Icon visibility is CSS-driven
  // off the [data-theme] attribute and does not need JS intervention.

  function syncToggles(theme) {
    var toggles = document.querySelectorAll(TOGGLE_SELECTOR);
    var label = theme === LIGHT ? ARIA_TO_DARK : ARIA_TO_LIGHT;
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('aria-label', label);
    }
  }

  // ---- Click handler ------------------------------------------------------

  function onToggleClick(e) {
    e.preventDefault();
    setTheme(currentTheme() === LIGHT ? DARK : LIGHT, true);
  }

  function bindToggles() {
    var toggles = document.querySelectorAll(TOGGLE_SELECTOR);
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].addEventListener('click', onToggleClick);
    }
    // Initial aria-label sync against the data-theme set by the head script.
    syncToggles(currentTheme());
  }

  // ---- System-preference fallback ----------------------------------------
  // If the user has NOT expressed an explicit preference (no value in
  // localStorage), follow the system when it changes. Once the user
  // clicks the toggle, the system listener is effectively dormant until
  // a future page load with cleared storage.

  function bindSystemListener() {
    if (!window.matchMedia) { return; }
    try {
      var mql = window.matchMedia('(prefers-color-scheme: light)');
      var handler = function (e) {
        if (readStored() === null) {
          setTheme(e.matches ? LIGHT : DARK, false);
        }
      };
      if (mql.addEventListener) {
        mql.addEventListener('change', handler);
      } else if (mql.addListener) {
        // Safari < 14 fallback
        mql.addListener(handler);
      }
    } catch (e) {
      /* matchMedia unsupported; ignore */
    }
  }

  // ---- Init ---------------------------------------------------------------

  function init() {
    bindToggles();
    bindSystemListener();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ---- Public API ---------------------------------------------------------

  window.ZHTheme = {
    get: currentTheme,
    set: function (theme) {
      if (theme === LIGHT || theme === DARK) {
        setTheme(theme, true);
      }
    },
    toggle: function () {
      setTheme(currentTheme() === LIGHT ? DARK : LIGHT, true);
    }
  };

})();

/* end of file ---------------------------------------------------------------- */
